<template>
    <div class="phone-number" @mouseleave="dropdown = false">
        <div class="supsis-dropdown">
            <div
                :key="'dropdown-phone-c'"
                class="supsis-dropdown-selected"
                @click="dropdown = !dropdown"
                v-if="country"
            >
                <span class="flag">{{ country.emoji }}</span>
                <span class="code">{{ country.dial_code }}</span>
            </div>
        </div>
        <div class="supsis-dropdown-menu" :class="dropdown && 'opened'" @mouseup="dropdown = true">
            <input
                v-if="dropdown"
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                class="supsis-dropdown-search"
                :placeholder="searchPlaceholder"
                @click.stop
            />
            <div
                v-if="dropdown"
                class="supsis-dropdown-item"
                v-for="c of filteredCountryPhoneInfos"
                :key="c.code"
                @click="onClick(c.code)"
            >
                {{ c.emoji }} ({{ c.dial_code }}) {{ c.name }}
            </div>
            <div v-if="dropdown && filteredCountryPhoneInfos.length === 0" class="supsis-dropdown-empty">
                {{ noResultText }}
            </div>
        </div>
        <md-input
            :autofocus="autofocus"
            :name="name"
            :v-validate="vValidate"
            class="phone-input"
            type="number"
            v-model="phoneNumber"
            :key="location.dial_code"
            @change="changePhoneNumber"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";

// Bolgesiz dil kodundan ulke tahmini (dusuk oncelikli geri dusus).
const LANG_TO_COUNTRY = {
    tr: "TR",
    en: "US",
    ru: "RU",
    de: "DE",
    fr: "FR",
    es: "ES",
    ar: "SA",
    it: "IT",
};

// Yaygin saat dilimleri; listede olmayan dilim bir sonraki sinyale birakiliyor.
const TZ_TO_COUNTRY = {
    "Europe/Istanbul": "TR",
    "Asia/Istanbul": "TR",
    "Europe/Berlin": "DE",
    "Europe/Paris": "FR",
    "Europe/Madrid": "ES",
    "Europe/Rome": "IT",
    "Europe/Moscow": "RU",
    "Europe/London": "GB",
    "Europe/Amsterdam": "NL",
    "America/New_York": "US",
    "America/Chicago": "US",
    "America/Denver": "US",
    "America/Los_Angeles": "US",
};

// md-input md-field'in sagladigi MdField nesnesine yazar; md-field disinda (karsilama karti) yoksa yerel nesne verilir
const localMdField = () => ({
    value: null,
    placeholder: false,
    disabled: false,
    required: false,
    counter: 0,
    maxlength: 0,
    focused: false,
    password: false,
    togglePassword: false,
    clear: false,
    hasInvalidValue: false,
});

export default {
    name: "SelectPhoneInput",
    components: {},
    inject: {
        MdField: { default: null },
    },
    provide() {
        return { MdField: this.MdField || this.localMdField };
    },
    props: {
        vValidate: {
            type: String,
            default: null,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            phoneNumber: null,
            country: null,
            dropdown: false,
            searchQuery: "",
            localMdField: localMdField(),
        };
    },
    methods: {
        onClick(code) {
            this.country = this.countryPhoneInfos.find((x) => x.code == code);
            this.dropdown = false;
            // Secim saklanmiyordu: ziyaretci her acilista ulkeyi yeniden seciyordu.
            try {
                localStorage.setItem("ls-phone-country", code);
            } catch (e) {}
            this.$emit("input", this.phoneNumber && this.country.dial_code + this.phoneNumber);
        },
        changePhoneNumber() {
            this.emitPhone();
        },
        emitPhone() {
            const dialCode = ((this.country || this.location) || {}).dial_code || "";
            this.$emit("input", this.phoneNumber ? dialCode + this.phoneNumber : this.phoneNumber);
        },
        getCountries() {
            return this.countryPhoneInfos.filter(Boolean).map((country) => ({
                value: country.code,
                label: `${country.emoji} ${country.name}`,
            }));
        },
        retrieveFromLocalStorage() {
            const storedPhoneNumber = localStorage.getItem("phoneNumber");
            if (storedPhoneNumber) {
                this.phoneNumber = storedPhoneNumber;
            }
        },
        // navigator.language "tr-TR" gibi geliyor: bolge parcasi ulke kodu.
        // Bolgesiz gelirse ("tr") dil kodundan cikariliyor.
        browserRegion() {
            const langs = [].concat(navigator.languages || [], navigator.language || []);
            for (const l of langs) {
                const parts = String(l || "").split("-");
                if (parts.length > 1 && parts[1].length === 2) return parts[1].toUpperCase();
            }
            for (const l of langs) {
                const base = String(l || "").split("-")[0].toLowerCase();
                if (LANG_TO_COUNTRY[base]) return LANG_TO_COUNTRY[base];
            }
            return null;
        },
        // Saat dilimi cok guclu bir sinyal ve her zaman dolu geliyor.
        timezoneRegion() {
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
                return TZ_TO_COUNTRY[tz] || null;
            } catch (e) {
                return null;
            }
        },
        findCountry(code) {
            if (!code) return null;
            return this.countryPhoneInfos.find((x) => String(x.code).toUpperCase() === String(code).toUpperCase());
        },
        // Oncelik: ziyaretcinin secimi > tarayici bolgesi > saat dilimi >
        // sunucu IP konumu > site dili. IP konumu bilerek geride: VPN, proxy ve
        // yerel gelistirmede yanlis ulke veriyor (127.0.0.1 -> US).
        resolveInitialCountry() {
            let stored = null;
            try {
                stored = localStorage.getItem("ls-phone-country");
            } catch (e) {}
            const siteLang = _.get(this.settings, "language.clientDefault");
            return (
                this.findCountry(stored) ||
                this.findCountry(this.browserRegion()) ||
                this.findCountry(this.timezoneRegion()) ||
                this.findCountry(this.location && this.location.code) ||
                this.findCountry(LANG_TO_COUNTRY[String(siteLang || "").toLowerCase()]) ||
                this.location
            );
        },
    },
    computed: {
        ...mapState({
            location: (state) => state.app.location,
            settings: (state) => state.setting.settings,
            countryPhoneInfos: (state) => state.app.countryPhoneInfos.filter((x) => !!x),
        }),
        filteredCountryPhoneInfos() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return this.countryPhoneInfos;
            return this.countryPhoneInfos.filter((c) => {
                const name = (c.name || "").toLowerCase();
                const code = (c.code || "").toLowerCase();
                const dialCode = (c.dial_code || "").toLowerCase();
                return name.indexOf(q) !== -1 || code.indexOf(q) !== -1 || dialCode.indexOf(q) !== -1;
            });
        },
        // Eski arama kok seviyeye bakiyordu, dosya "message" altinda yuvali:
        // $te hep false donup her dilde Turkce metin cikiyordu.
        searchPlaceholder() {
            if (this.$te && this.$te("message.searchCountry")) return this.$t("message.searchCountry");
            if (this.$te && this.$te("search")) return this.$t("search");
            return "Ara...";
        },
        noResultText() {
            if (this.$te && this.$te("message.noResultFound")) return this.$t("message.noResultFound");
            if (this.$te && this.$te("noResult")) return this.$t("noResult");
            return "Sonuç bulunamadı";
        },
    },
    mounted() {
        this.country = this.resolveInitialCountry();
        this.retrieveFromLocalStorage();
    },
    watch: {
        location: {
            handler() {
                // Ziyaretcinin kendi secimi sunucu tahminini ezmemeli.
                let stored = null;
                try {
                    stored = localStorage.getItem("ls-phone-country");
                } catch (e) {}
                if (!stored) this.country = this.resolveInitialCountry();
            },
            deep: true,
        },
        countryPhoneInfos: {
            handler() {
                // Ulke listesi sonradan geliyor: geldiginde yeniden cozumleniyor.
                if (this.countryPhoneInfos.length) this.country = this.resolveInitialCountry();
            },
        },
        phoneNumber: {
            handler(newPhoneNumber) {
                localStorage.setItem("phoneNumber", newPhoneNumber);
                this.emitPhone();
            },
            deep: true,
        },
        dropdown(isOpen) {
            if (isOpen) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            } else {
                this.searchQuery = "";
            }
        },
    },
};
</script>

<style lang="scss">
.v2 {
    .phone-number {
        width: 100%;
        display: flex;
        justify-content: start;
        align-items: center;
        // Menunun yerlesim referansi. Yoksa en yakin konumlanmis ata neyse ona
        // gore hizalaniyordu: karsilama kartinda liste telefon girisinin uzerine
        // biniyor, formu kullanilamaz hale getiriyordu.
        position: relative;

        .supsis-dropdown {
            position: relative;
        }

        .supsis-dropdown-menu {
            border-radius: var(--sps-radius-sm);
            width: 100%;
            position: absolute;
            // Sabit 30px alanin ust bosluguyla etiket yuksekligini saymiyordu.
            top: calc(100% + 4px);
            left: 0;
            background-color: #fff;
            height: 0px;
            opacity: 0;
            // Liste beyaz zeminli kartta sinirsiz duruyordu; kendi yuzeyi olsun.
            border: 1px solid rgba(15, 23, 42, 0.12);
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
            box-sizing: border-box;
            overflow-y: auto;
            overflow-x: hidden;
            z-index: 999;
            padding: 5px 10px;
            transition: all 0.2s;

            &.opened {
                opacity: 1;
                height: 170px !important;
            }

            .supsis-dropdown-search {
                position: sticky;
                top: 0;
                width: 100%;
                box-sizing: border-box;
                padding: 5px 8px;
                margin-bottom: 5px;
                border: 1px solid #ddd;
                border-radius: var(--sps-radius-xs);
                outline: none;
                font-size: 12px;
                color: #000 !important;
                background-color: #fff;
                z-index: 1;
            }

            .supsis-dropdown-empty {
                padding: 6px 7px;
                color: #888 !important;
                font-size: 12px;
                text-align: center;
                user-select: none;
            }

            .supsis-dropdown-item {
                padding: 3px 7px;
                overflow: hidden;
                user-select: none;
                color: #000 !important;
                font-size: 12px;

                &:hover {
                    background-color: rgb(222, 222, 222);
                }
            }
        }

        .supsis-dropdown-selected {
            user-select: none;
            cursor: pointer;
            height: 32px !important;
            display: flex !important;
            align-items: center !important;
            text-decoration: none !important;
            color: #fff !important;
            background: transparent !important;

            .flag {
                font-size: 18px;
                margin-right: 5px;
                color: var(--form-input-label) !important;
            }

            .code {
                font-weight: 400;
                flex: 2;
                color: var(--form-input-label) !important;
            }
        }

        .phone-input::-webkit-outer-spin-button,
        .phone-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        input.phone-input {
            color: var(--form-input-label) !important;
            background: transparent !important;
            outline: none !important;
            border: none !important;
            box-shadow: none !important;
            width: 100%;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/V2/popup-chat/SelectPhoneInput.vue